<!--
// app/components/region-stats/cards-featured-stats/CardPlayerRankingGeneralNoPartner.vue
-->
<script setup lang="ts">
import type { RankingsData } from "~/src/player/domain/Player";
import { useImages } from "~/app/composables/images";
import { useRegions } from "~/app/composables/regions";
import { setAliasUrl } from "~/src/Shared/utils";
import PlaceholderTeamShield from "~/app/components/pages/region-stats/cards-featured-stats/PlaceholderTeamShield.vue";

type Props = {
  seasonId: number | string;
  parameterString: string;
  pageStatsLink: string;
  preTitleStat?: string;
  titleStat: string;
  tableStatTitle: string;
  isStats?: boolean; // Usage is in /stats
  sortedRanking?: boolean;
  sortOrder?: "ASC" | "DESC";
  orderBy?: "ranking" | "rankingAVG";
};

const props = defineProps<Props>();

const localePath = useLocalePath();
const route = useRoute();
const { regionParam, isRegionHome } = useRegions();
const { playerPlaceholderPath, videoBgFeaturedStatsPath, posterBgFeaturedStatsPath } = useImages();

const queries = computed(() => {
  return route.query ? route.query : undefined;
});

const videoBg = ref<HTMLVideoElement | null>(null);

let isPlaying = false;
let playPromise = null;

const playVideoBg = () => {
  if (videoBg.value && !isPlaying) {
    playPromise = videoBg.value.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          isPlaying = true;
        })
        .catch(error => {
          console.warn("play() failed:", error);
        });
    }
  }
};

const pauseVideoBg = () => {
  if (videoBg.value && isPlaying) {
    if (playPromise !== null) {
      playPromise.finally(() => {
        videoBg.value.pause();
        isPlaying = false;
      });
    } else {
      videoBg.value.pause();
      isPlaying = false;
    }
  }
};

const { data: topPlayer }: RankingsData[] = await useFetch(
  `/api/v1/competition/seasons/${props.seasonId}/players/stats/${props.parameterString}`,
  {
    query: { page: 0, orderBy: props.orderBy, sortOrder: props.sortOrder },
    transform: input => {
      if (input && props.parameterString === "GOL-S") {
        const goalkeepers = input.filter((r: RankingsData) => r.player.role === "goalkeeper");
        return goalkeepers.slice(0, 3);
      } else {
        return input.slice(0, 3);
      }
    },
  }
);
</script>

<template>
  <div
    class="flex flex-col max-w-[343px] w-full min-w-full lg:max-w-none 2xl:w-auto 2xl:aspect-[325/568]"
    @mouseenter="playVideoBg"
    @mouseleave="pauseVideoBg"
  >
    <div class="card-player-image-container">
      <nuxt-link
        :to="
          localePath({
            name: `region-stats-${pageStatsLink}`,
            params: { region: regionParam },
            query: queries,
          })
        "
        :no-prefetch="!!isRegionHome"
      >
        <video
          :src="videoBgFeaturedStatsPath"
          :poster="posterBgFeaturedStatsPath"
          class="absolute top-0 left-0 right-0 bottom-0 min-h-[463px] sm:min-h-[546px] object-cover object-top"
          ref="videoBg"
          width="100%"
          height="100%"
          loop
          muted
        ></video>
        <div class="image-container">
          <NuxtImg
            :src="
              topPlayer.length && topPlayer[0]?.player?.image?.url
                ? setAliasUrl(topPlayer[0].player.image.url)
                : playerPlaceholderPath
            "
            :alt="topPlayer && topPlayer.length ? topPlayer[0]?.player?.shortName : ''"
            class="object-top object-cover min-h-[463px] sm:min-h-[546px]"
            width="456"
            height="642"
            sizes="256px sm:472px md:325px"
          />
        </div>
        <div class="gradient-container">
          <div>
            <div class="pre-title-stat">{{ preTitleStat }}</div>
            <div class="title-stat">{{ titleStat }}</div>
          </div>
        </div>
      </nuxt-link>

      <div class="mt-4 table-first-stats absolute min-w-max left-0 bottom-0 right-0 p-1">
        <div class="flex items-center justify-end text-gray-400 text-[8.5px] sm:text-[10px] uppercase px-1">
          <span class="w-1/6 text-center min-w-min me-1">{{ $t("cards.stats.team.text") }}</span>
          <span class="w-4/6 text-left rtl:text-right">{{ $t("cards.stats.name.text") }}</span>
          <span class="w-1/6 text-center">{{ tableStatTitle }}</span>
        </div>
        <div
          v-for="(ranking, i) in topPlayer?.length ? topPlayer : 3"
          :key="ranking.id || i"
          class="flex items-center justify-end text-sm font-bold px-1 bg-gray-800 bg-opacity-50 rounded-md mt-1"
        >
          <div class="w-1/6 min-w-min me-1 logo-team-container">
            <NuxtImg
              v-if="ranking?.team?.logo?.url"
              :src="setAliasUrl(ranking.team.logo.url)"
              alt="Team"
              class="object-contain h-8 w-8 lg:w-10 lg:h-10"
              loading="lazy"
              height="40"
              width="40"
            />
            <span v-else class="text-gray-700"
              ><placeholder-team-shield :width="40" :height="40"></placeholder-team-shield
            ></span>
          </div>
          <component
            :is="isStats ? 'h2' : 'div'"
            class="w-4/6 text-left rtl:text-right me-auto text-xs sm:text-sm !leading-[1.1]"
          >
            {{ ranking?.player?.shortName || $t("general.player.word") }}
          </component>
          <div class="w-1/6 flex items-center justify-center">
            {{ ranking && ranking.total ? Number(ranking.total) : 0 }}
          </div>
        </div>
      </div>
    </div>

    <div class="text-center py-2" v-if="regionParam && pageStatsLink">
      <nuxt-link
        :to="
          localePath({
            name: `region-stats-${pageStatsLink}`,
            params: { region: regionParam },
            query: queries,
          })
        "
        class="see-more-link"
        :no-prefetch="!!isRegionHome"
        >{{ $t("cards.stats.seeMore.link") }}
      </nuxt-link>
    </div>
  </div>
</template>

<style scoped>
.card-player-image-container {
  @apply min-h-[463px] sm:min-h-[546px] px-1 py-2 rounded-lg relative overflow-hidden bg-no-repeat bg-cover bg-center;
}

.image-container {
  @apply h-full absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-start items-center pt-5;
}

.gradient-container {
  @apply absolute min-w-full min-h-full top-0 right-0 left-0 bottom-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.0)40%,rgba(0,0,0,1)100%)] px-1 py-2 flex flex-col justify-between;
}

.pre-title-stat {
  @apply text-primary-light text-center text-sm uppercase font-['Archivo_Expanded'] font-bold leading-[14px];
}

.title-stat {
  @apply text-black text-center text-lg sm:text-2xl font-bold uppercase font-['Archivo_Expanded'] leading-none;
  -webkit-text-stroke: 1px theme("colors.primary.light");
  text-stroke: 1px theme("colors.primary.light");
}

.logo-team-container {
  @apply flex flex-col justify-center items-center bigsm:p-0.5;
}

.see-more-link {
  @apply text-xs text-gray-400;
}
</style>
